import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import DatePicker from 'react-datepicker';
import api from '../../../utils/api';
import { ChecklistDisplay, ChecklistCompletion } from '../../Common/TaskChecklist';
import 'react-datepicker/dist/react-datepicker.css';
import './AvailableTasks.css';

// ── Mapping helpers (mirrors mobile orderService.js) ──
const SERVICE_TYPES = { home: 'Home Cleaning', commercial: 'Commercial Cleaning', moveinout: 'Move-in/Move-out' };
const CLEANING_CATEGORIES = { standard: 'Standard Cleaning', main: 'Main Cleaning', adhoc: 'Ad hoc Cleaning' };
const KNOWN_AS_NEEDED = ['wipingFrames', 'limeShower', 'vacuumFurniture', 'cobwebs'];
const KNOWN_MAIN_EXTRAS = [
  'wipingFrames', 'wipingWindowEdges', 'wipingCeilingEdges', 'windowCleaning',
  'blindsCleaned', 'furnitureVacuumed', 'behindFurniture', 'cleaningWallsCeilings',
  'underCarpets', 'mirrorsGlass', 'ceilingLights', 'closedSpaces',
  'insideCupboards', 'woodenFloors', 'ovenFridge',
];
const KNOWN_ADHOC = ['standardWithout', 'vacuumAndFloor', 'bathroomAndFloor', 'largeHome'];
const KNOWN_EXTRA_TARGETED = ['animalHair', 'smoking'];
const EQUIPMENT_PREFIX = 'equipment:';
const EQUIPMENT_KEYS = ['cleaningAgents', 'cloth', 'vacuumCleaner', 'mop', 'specialProducts'];
const ALL_KNOWN = new Set([...KNOWN_AS_NEEDED, ...KNOWN_MAIN_EXTRAS, ...KNOWN_ADHOC, ...KNOWN_EXTRA_TARGETED]);

const mapServiceType = (cleaningType) => {
  if (cleaningType === 'residential') return 'home';
  if (cleaningType === 'move') return 'moveinout';
  if (cleaningType === 'deep') return 'home';
  return cleaningType;
};
const mapCleaningCategory = (cleaningType) => (cleaningType === 'deep' ? 'main' : 'standard');

const formatClientDisplay = (client) => {
  if (!client) return 'N/A';
  return `${client.name || '-'} • ${client.city || '-'} • ${client.zipCode || '-'}`;
};

const AvailableTasks = ({ userId }) => {
    const getScheduleTimestamp = (task) => {
      if (!task?.date) return 0;
      const dateOnly = String(task.date).split('T')[0];
      const timeValue = task.time || '00:00';
      const timestamp = new Date(`${dateOnly}T${timeValue}`).getTime();
      return Number.isNaN(timestamp) ? 0 : timestamp;
    };

  const { t } = useLanguage();
  const { showToast } = useToast();
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [recurringSiblings, setRecurringSiblings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  // Helper function to extract numeric part from task ID
  const getTaskIdNumber = (taskId) => {
    if (!taskId) return '';
    // If it's already a number (SQL Server), return it directly
    if (typeof taskId === 'number') {
      return taskId.toString();
    }
    // Convert to string if needed
    const idStr = String(taskId);
    // For MongoDB ObjectId (24 characters), return the last 6 characters
    if (idStr.length === 24) {
      return idStr.slice(-6);
    }
    // For other formats, try to extract number or return as is
    const match = idStr.match(/-(\d+)$/);
    return match ? match[1] : idStr;
  };

  useEffect(() => {
    fetchAssignedTasks();
  }, [userId, startDate, endDate]);

  // Fetch recurring siblings when a recurring task detail is opened
  useEffect(() => {
    const groupId = selectedTask?.recurrenceGroupId;
    if (!groupId) { setRecurringSiblings([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/tasks', { params: {} });
        if (cancelled) return;
        const all = Array.isArray(res.data?.data) ? res.data.data : [];
        const siblings = all
          .filter(t => String(t.recurrenceGroupId) === String(groupId))
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        setRecurringSiblings(siblings);
      } catch { if (!cancelled) setRecurringSiblings([]); }
    })();
    return () => { cancelled = true; };
  }, [selectedTask?.recurrenceGroupId]);

  const fetchAssignedTasks = async () => {
    try {
      setLoading(true);
      const params = {};
      
      // Apply date filter if dates are selected
      if (startDate) {
        params.startDate = startDate.toISOString();
      }
      if (endDate) {
        params.endDate = endDate.toISOString();
      }
      
      const response = await api.get('/tasks/assigned', { params });
      let tasks = response.data.data;

      setAssignedTasks(groupRecurringTasks(tasks));
    } catch (error) {
      console.error('Error fetching assigned tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupRecurringTasks = (items) => {
    const groups = new Map();
    const singles = [];

    (Array.isArray(items) ? items : []).forEach((task) => {
      const groupId = task.recurrenceGroupId;
      if (!groupId) {
        singles.push({ ...task, recurrenceCount: 1, isRecurring: false });
        return;
      }

      // Accepted tasks are shown individually (expanded after cleaner accepts)
      if (task.status === 'accepted') {
        singles.push({ ...task, isRecurring: true, recurrenceCount: 1 });
        return;
      }

      // Assigned tasks are grouped into one entry
      const key = String(groupId);
      const existing = groups.get(key);
      if (!existing) {
        groups.set(key, { ...task, isRecurring: true, recurrenceCount: 1, _count: 1 });
        return;
      }

      existing._count += 1;
      existing.recurrenceCount = Math.max(existing.recurrenceCount || 1, existing._count);

      const existingDate = existing.date ? new Date(existing.date) : null;
      const candidateDate = task.date ? new Date(task.date) : null;
      if (candidateDate && (!existingDate || candidateDate < existingDate)) {
        Object.assign(existing, {
          ...task,
          isRecurring: true,
          recurrenceCount: existing.recurrenceCount,
          _count: existing._count,
        });
      }
    });

    const grouped = Array.from(groups.values()).map((task) => {
      const normalized = { ...task };
      delete normalized._count;
      return normalized;
    });

    return [...grouped, ...singles].sort((a, b) => getScheduleTimestamp(a) - getScheduleTimestamp(b));
  };

  const handleAcceptTask = async (taskId) => {
    try {
      await api.post(`/tasks/${taskId}/accept`);
      showToast(t('cleaner.taskAccepted', 'Task accepted successfully!'), 'success');
      fetchAssignedTasks();
    } catch (error) {
      console.error('Error accepting task:', error);
      const errorMessage = error.response?.data?.message || t('cleaner.errorAccepting', 'Error accepting task. Please try again.');
      showToast(errorMessage, 'error');
    }
  };

  const handleCompleteTask = async (taskId, checklistCompleted = []) => {
    try {
      await api.post(`/tasks/${taskId}/complete`, {
        checklistCompleted
      });
      showToast(t('cleaner.taskCompleted', 'Task marked as completed!'), 'success');
      fetchAssignedTasks();
    } catch (error) {
      console.error('Error completing task:', error);
      const errorMessage = error.response?.data?.message || t('cleaner.errorCompleting', 'Error completing task. Please try again.');
      showToast(errorMessage, 'error');
    }
  };

  if (loading) {
    return <div className="loading">{t('common.loading', 'Loading...')}</div>;
  }

  return (
    <div className="available-tasks">
      <div className="available-tasks-header">
        <h2>{t('cleaner.myAssignedTasks', 'My Assigned Tasks')}</h2>
        <div className="date-filter">
          <div className="date-filter-group">
            <label>{t('common.from', 'From')}:</label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              placeholderText={t('common.startDate', 'Start date')}
              dateFormat="dd,MM,yyyy"
              className="date-picker-input"
            />
          </div>
          <div className="date-filter-group">
            <label>{t('common.to', 'To')}:</label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              placeholderText={t('common.endDate', 'End date')}
              dateFormat="dd,MM,yyyy"
              className="date-picker-input"
            />
          </div>
          {(startDate || endDate) && (
            <button 
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
              }}
            >
              {t('common.clearFilter', 'Clear Filter')}
            </button>
          )}
        </div>
      </div>
      
      {assignedTasks.length === 0 ? (
        <p className="no-tasks">{t('cleaner.noTasksAssigned', 'No tasks assigned yet.')}</p>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="at-table-wrapper">
            <table className="at-table">
              <thead>
                <tr>
                  <th>{t('admin.taskId', 'Task ID')}</th>
                  <th>{t('auth.address', 'Address')}</th>
                  <th>{t('client.date', 'Date')}</th>
                  <th>{t('client.time', 'Time')}</th>
                  <th>{t('client.hours', 'Hours')}</th>
                  <th>{t('admin.type', 'Type')}</th>
                  <th>{t('admin.frequency', 'Frequency')}</th>
                  <th>{t('status.status', 'Status')}</th>
                  <th>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {assignedTasks.map(task => {
                  const taskId = task._id || task.id;
                  return (
                    <tr key={taskId}>
                      <td>#{getTaskIdNumber(taskId)}</td>
                      <td>{task.address}</td>
                      <td>{new Date(task.date).toLocaleDateString('en-GB')}</td>
                      <td>{task.time}</td>
                      <td>{task.hours}</td>
                      <td>{SERVICE_TYPES[mapServiceType(task.cleaningType)] || task.cleaningType || 'N/A'}</td>
                      <td>
                        {task.isRecurring ? (
                          <span className="at-recurring-badge">
                            🔄 Every {task.recurrenceEvery ?? 1} wk(s){task.recurrenceCount > 1 ? ` (${task.recurrenceCount})` : ''}
                          </span>
                        ) : 'Once'}
                      </td>
                      <td>
                        <span className={`at-status at-status-${task.status}`}>{task.status}</span>
                      </td>
                      <td>
                        <div className="at-action-buttons">
                          <button className="btn btn-primary btn-sm" onClick={() => setSelectedTask(task)}>
                            {t('common.details', 'Details')}
                          </button>
                          {task.status === 'assigned' && (
                            <button className="btn btn-success btn-sm" onClick={() => handleAcceptTask(taskId)}>
                              {t('cleaner.acceptTask', 'Accept')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="at-cards">
            {assignedTasks.map(task => {
              const taskId = task._id || task.id;
              return (
                <div key={taskId} className="at-card">
                  <div className="at-card-header">
                    <span className="at-card-id">#{getTaskIdNumber(taskId)}</span>
                    <span className={`at-status at-status-${task.status}`}>{task.status}</span>
                  </div>
                  <div className="at-card-details">
                    <div className="at-card-row">
                      <span className="at-card-label">{t('auth.address', 'Address')}:</span>
                      <span className="at-card-value">{task.address}</span>
                    </div>
                    <div className="at-card-row">
                      <span className="at-card-label">{t('client.date', 'Date')}:</span>
                      <span className="at-card-value">{new Date(task.date).toLocaleDateString('en-GB')}</span>
                    </div>
                    <div className="at-card-row">
                      <span className="at-card-label">{t('client.time', 'Time')}:</span>
                      <span className="at-card-value">{task.time}</span>
                    </div>
                    <div className="at-card-row">
                      <span className="at-card-label">{t('client.hours', 'Hours')}:</span>
                      <span className="at-card-value">{task.hours}</span>
                    </div>
                    <div className="at-card-row">
                      <span className="at-card-label">{t('admin.serviceType', 'Service Type')}:</span>
                      <span className="at-card-value">{SERVICE_TYPES[mapServiceType(task.cleaningType)] || task.cleaningType || 'N/A'}</span>
                    </div>
                    {task.isRecurring && (
                      <div className="at-card-row">
                        <span className="at-card-label">{t('admin.frequency', 'Frequency')}:</span>
                        <span className="at-card-value at-recurring-badge">
                          🔄 Every {task.recurrenceEvery ?? 1} week(s){task.recurrenceCount > 1 ? ` (${task.recurrenceCount} tasks)` : ''}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="at-card-actions">
                    <button className="btn btn-primary btn-sm" onClick={() => setSelectedTask(task)}>
                      {t('common.details', 'Details')}
                    </button>
                    {task.status === 'assigned' && (
                      <button className="btn btn-success btn-sm" onClick={() => handleAcceptTask(taskId)}>
                        {t('cleaner.acceptTask', 'Accept')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Details Modal */}
      {selectedTask && (
        <div className="at-modal-overlay" onClick={() => setSelectedTask(null)}>
          <div className="at-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="at-modal-header">
              <h3>{t('common.details', 'Details')} - #{getTaskIdNumber(selectedTask._id || selectedTask.id)}</h3>
              <button className="at-close-btn" onClick={() => setSelectedTask(null)}>&times;</button>
            </div>
            <div className="at-modal-body">
              {/* Status */}
              <div className="at-detail-status">
                <span className={`at-status at-status-${selectedTask.status}`}>{selectedTask.status}</span>
              </div>

              {/* Service Type */}
              <div className="at-detail-section">
                <h4>{t('newOrder.serviceType', 'Service Type')}</h4>
                <p>{SERVICE_TYPES[mapServiceType(selectedTask.cleaningType)] || selectedTask.cleaningType}</p>
              </div>

              {/* Client Information */}
              {selectedTask.client && (
                <div className="at-detail-section">
                  <h4>{t('cleaner.clientInfo', 'Client Information')}</h4>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">👤</span>
                    <span>{selectedTask.client.name || '-'}</span>
                  </div>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">🏙️</span>
                    <span><strong>{t('auth.city', 'City')}:</strong> {selectedTask.client.city || '-'}</span>
                  </div>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">📮</span>
                    <span><strong>{t('auth.zipCode', 'Zip Code')}:</strong> {selectedTask.client.zipCode || '-'}</span>
                  </div>
                  {selectedTask.address && (
                    <div className="at-detail-row">
                      <span className="at-detail-icon">🏠</span>
                      <span>{selectedTask.address}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Recurring Information */}
              {(selectedTask.isRecurring || selectedTask.recurrenceGroupId) && (
                <div className="at-detail-section">
                  <h4>{t('orders.recurringInfo', 'Recurring Information')}</h4>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">🔄</span>
                    <span>Every {selectedTask.recurrenceEvery ?? 1} week(s){selectedTask.recurrenceCount > 1 ? ` (${selectedTask.recurrenceCount} tasks)` : ''}</span>
                  </div>
                  {Array.isArray(selectedTask.recurrenceDays) && selectedTask.recurrenceDays.length > 0 && (
                    <div className="at-detail-row">
                      <span className="at-detail-icon">📅</span>
                      <span>{selectedTask.recurrenceDays.map(d => ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d] || d).join(', ')}</span>
                    </div>
                  )}
                  {selectedTask.recurrenceUntil && (
                    <div className="at-detail-row">
                      <span className="at-detail-icon">🏁</span>
                      <span>Until {selectedTask.recurrenceUntil}</span>
                    </div>
                  )}
                  {recurringSiblings.length > 1 && (
                    <div style={{ marginTop: '12px', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                      <strong style={{ fontSize: '0.9em' }}>{t('orders.upcomingDates', 'Scheduled Dates')}</strong>
                      <div style={{ marginTop: '6px' }}>
                        {recurringSiblings.map((s, idx) => {
                          const sDate = s.date ? new Date(s.date) : null;
                          if (sDate) sDate.setHours(0, 0, 0, 0);
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const isPast = sDate && sDate < today;
                          const isCurrent = String(s.id) === String(selectedTask.id);
                          const statusLabel = s.status ? s.status.charAt(0).toUpperCase() + s.status.slice(1) : '';
                          return (
                            <div key={s.id || idx} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              backgroundColor: isCurrent ? '#e6f7ff' : 'transparent',
                              opacity: isPast ? 0.5 : 1,
                            }}>
                              <span>{s.status === 'completed' ? '✅' : isPast ? '⭕' : '🔵'}</span>
                              <span style={{ flex: 1, textDecoration: isPast ? 'line-through' : 'none', color: isPast ? '#a0aec0' : '#2d3748' }}>
                                {sDate ? sDate.toLocaleDateString('en-GB') : '—'}
                              </span>
                              <span style={{ fontSize: '0.8em', fontWeight: 600, textTransform: 'uppercase', color: s.status === 'completed' ? '#38a169' : s.status === 'accepted' ? '#2c7a7b' : '#ed8936' }}>
                                {statusLabel}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Title */}
              {selectedTask.title && (
                <div className="at-detail-section">
                  <h4>{t('orders.title', 'Title')}</h4>
                  <p>{selectedTask.title}</p>
                </div>
              )}

              {/* Booking Details */}
              <div className="at-detail-section">
                <h4>{t('newOrder.bookingDetails', 'Booking Details')}</h4>
                <div className="at-detail-row">
                  <span className="at-detail-icon">📍</span>
                  <span>{selectedTask.address || '—'}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">📅</span>
                  <span>{new Date(selectedTask.date).toLocaleDateString('en-GB')}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">🕐</span>
                  <span>{selectedTask.allDay ? t('orders.allDay', 'All Day') : (selectedTask.time || '—')}{selectedTask.endTime ? ` – ${selectedTask.endTime}` : ''}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">⏳</span>
                  <span>{selectedTask.hours || '–'} {t('admin.hours', 'hours')}</span>
                </div>
              </div>

              {/* Dynamic checklist-derived sections */}
              {(() => {
                const checklist = Array.isArray(selectedTask.checklist) ? selectedTask.checklist : [];
                const asNeeded = checklist.filter(i => KNOWN_AS_NEEDED.includes(i));
                const mainExtras = checklist.filter(i => KNOWN_MAIN_EXTRAS.includes(i));
                const adhoc = checklist.filter(i => KNOWN_ADHOC.includes(i));
                const equipmentSelected = EQUIPMENT_KEYS.filter(k => checklist.includes(`${EQUIPMENT_PREFIX}${k}`));
                const extraTargeted = [];
                if (checklist.includes('animalHair')) extraTargeted.push(t('newOrder.animalHair', 'Animal hair'));
                if (checklist.includes('smoking')) extraTargeted.push(t('newOrder.smoking', 'Smoking'));
                const remaining = checklist.filter(item => typeof item === 'string' && !ALL_KNOWN.has(item) && !item.startsWith(EQUIPMENT_PREFIX));

                const equipmentLabels = {
                  cleaningAgents: t('newOrder.cleaningAgents', 'Cleaning agents'),
                  cloth: t('newOrder.cloth', 'Cloth'),
                  vacuumCleaner: t('newOrder.vacuumCleaner', 'Vacuum cleaner'),
                  mop: t('newOrder.mop', 'Mop'),
                  specialProducts: t('newOrder.specialProducts', 'Special products'),
                };

                const renderChecklistSection = (items, title) => items.length > 0 ? (
                  <div className="at-detail-section" key={title}>
                    <h4>{title}</h4>
                    <ul className="at-detail-list">
                      {items.map((item, i) => <li key={i}>✅ {item}</li>)}
                    </ul>
                  </div>
                ) : null;

                return (
                  <>
                    {renderChecklistSection(asNeeded, t('newOrder.asNeeded', 'As Needed'))}
                    {renderChecklistSection(mainExtras, t('newOrder.mainCleaningExtras', 'Main Cleaning Extras'))}
                    {renderChecklistSection(adhoc, t('newOrder.adhocOptions', 'Ad hoc Options'))}
                    {equipmentSelected.length > 0 && (
                      <div className="at-detail-section">
                        <h4>{t('newOrder.equipment', 'Equipment')}</h4>
                        <ul className="at-detail-list">
                          {equipmentSelected.map((k) => <li key={k}>✅ {equipmentLabels[k] || k}</li>)}
                        </ul>
                      </div>
                    )}
                    {renderChecklistSection(extraTargeted, t('newOrder.extraTargeted', 'Extra Targeted'))}
                    {remaining.length > 0 && (
                      <div className="at-detail-section">
                        <h4>{t('client.whatNeedsToBeDone', 'What needs to be done')}</h4>
                        <ChecklistDisplay items={remaining} completed={selectedTask.status === 'completed' ? selectedTask.checklistCompleted : undefined} />
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Comments */}
              {selectedTask.comments && (
                <div className="at-detail-section">
                  <h4>{t('newOrder.additionalComments', 'Comments')}</h4>
                  <div className="at-detail-comments">{selectedTask.comments}</div>
                </div>
              )}

              {/* Order Date */}
              {selectedTask.createdAt && (
                <div className="at-detail-meta">
                  <span className="at-detail-meta-label">{t('orders.orderDate', 'Order Date')}</span>
                  <span>{new Date(selectedTask.createdAt).toLocaleDateString('en-GB')}</span>
                </div>
              )}
            </div>
            <div className="at-modal-actions">
              {selectedTask.status === 'assigned' && (
                <button className="btn btn-success" onClick={() => { handleAcceptTask(selectedTask._id || selectedTask.id); setSelectedTask(null); }}>
                  {t('cleaner.acceptTask', 'Accept Task')}
                </button>
              )}
              {selectedTask.status === 'accepted' && (
                <TaskCompletionForm
                  task={selectedTask}
                  taskId={selectedTask._id || selectedTask.id}
                  onComplete={(id, cl) => { handleCompleteTask(id, cl); setSelectedTask(null); }}
                />
              )}
              {selectedTask.status === 'completed' && (
                <span className="completed-badge">{t('cleaner.taskCompletedBadge', 'Task Completed')}</span>
              )}
              <button className="btn btn-secondary" onClick={() => setSelectedTask(null)}>
                {t('common.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TaskCompletionForm = ({ task, taskId, onComplete }) => {
  const { t } = useLanguage();
  const [showForm, setShowForm] = useState(false);
  const [checklistCompleted, setChecklistCompleted] = useState([]);
  const checklist = task?.checklist || [];

  const handleSubmit = (e) => {
    e.preventDefault();
    onComplete(taskId, checklistCompleted);
    setChecklistCompleted([]);
    setShowForm(false);
  };

  if (!showForm) {
    return (
      <button
        className="btn btn-primary"
        onClick={() => setShowForm(true)}
      >
        {t('cleaner.markAsCompleted', 'Mark as Completed')}
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="completion-form">
      {checklist.length > 0 && (
        <ChecklistCompletion
          items={checklist}
          completed={checklistCompleted}
          onChange={setChecklistCompleted}
        />
      )}
      <div className="form-actions">
        <button type="submit" className="btn btn-success">{t('cleaner.completeTask', 'Complete Task')}</button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            setShowForm(false);
            setChecklistCompleted([]);
          }}
        >
          {t('common.cancel', 'Cancel')}
        </button>
      </div>
    </form>
  );
};

export default AvailableTasks;



